import { useEffect, useState } from "react";

import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { ServiceConstants } from "constant";
import { ContractPaymentService } from "services";
import { Bank, ContractPaymentOptionsResponse, ContractPaymentRequest, ContractPaymentResponse } from "types";

const {
  REQUEST_CONTRACT_PAYMENT_LIST_ID,
  REQUEST_CONTRACT_PAYMENT_OPTIONS_ID,
  REQUEST_CONTRACT_PAYMENT_CREATE_ID,
  REQUEST_CONTRACT_PAYMENT_DELETE_ID,
  REQUEST_PENDING,
  REQUEST_SUCCESS,
  REQUEST_ERROR,
} = ServiceConstants;

export const useContractPayments = (contractId: number | undefined) => {
  const { t } = useTranslation();
  const [contractPayments, setContractPayments] = useState<ContractPaymentResponse[]>([]);
  const [contractPaymentTypes, setContractPaymentTypes] = useState<string[]>([]);
  const [isAddingContractPayment, setIsAddingContractPayment] = useState(false);
  const [newContractPaymentType, setNewContractPaymentType] = useState<string | null>(null);
  const [newContractPaymentBank, setNewContractPaymentBank] = useState<Bank | null>(null);
  const [newContractPaymentContractNumber, setNewContractPaymentContractNumber] = useState<string>("");
  const [newContractPaymentAmount, setNewContractPaymentAmount] = useState<number | undefined>(undefined);
  const [deletingContractPaymentId, setDeletingContractPaymentId] = useState<number | undefined>(undefined);

  const contractPaymentsRequestKey = contractId
    ? REQUEST_CONTRACT_PAYMENT_LIST_ID + contractId
    : undefined;

  const contractPaymentsResponse = useSelector(
    (state: any) =>
      contractPaymentsRequestKey ? state.service.object[contractPaymentsRequestKey] : undefined
  );
  const contractPaymentOptionsResponse = useSelector(
    (state: any) => state.service.object[REQUEST_CONTRACT_PAYMENT_OPTIONS_ID]
  );
  const contractPaymentCreateResponse = useSelector(
    (state: any) => state.service.object[REQUEST_CONTRACT_PAYMENT_CREATE_ID]
  );
  const serviceObjects = useSelector((state: any) => state.service.object);

  // Fetch payment options once on mount
  useEffect(() => {
    ContractPaymentService.getContractPaymentOptions();
  }, []);

  // Fetch payments when contractId becomes available
  useEffect(() => {
    if (contractId) {
      ContractPaymentService.getContractPayments(contractId);
    }
  }, [contractId]);

  // Handle payment options response
  useEffect(() => {
    if (contractPaymentOptionsResponse?.data) {
      const response = contractPaymentOptionsResponse.data as ContractPaymentOptionsResponse;
      setContractPaymentTypes(Array.isArray(response?.type) ? response.type : []);
    }
  }, [contractPaymentOptionsResponse]);

  // Handle payments list response
  useEffect(() => {
    if (contractPaymentsResponse?.data && Array.isArray(contractPaymentsResponse.data)) {
      setContractPayments(contractPaymentsResponse.data as ContractPaymentResponse[]);
    }
  }, [contractPaymentsResponse]);

  // Handle create response
  useEffect(() => {
    if (!contractId) return;

    if (contractPaymentCreateResponse?.status === REQUEST_SUCCESS) {
      setIsAddingContractPayment(false);
      setNewContractPaymentType(null);
      setNewContractPaymentBank(null);
      setNewContractPaymentContractNumber("");
      setNewContractPaymentAmount(undefined);
      ContractPaymentService.getContractPayments(contractId);
    }

    if (contractPaymentCreateResponse?.status === REQUEST_ERROR) {
      notifications.show({
        title: t("bankSelection.paymentStructure.title"),
        message: t("error.unknown"),
        color: "red",
      });
    }
  }, [contractId, contractPaymentCreateResponse, t]);

  // Handle delete response
  useEffect(() => {
    if (!contractId || !deletingContractPaymentId) return;

    const deleteResponse = serviceObjects[REQUEST_CONTRACT_PAYMENT_DELETE_ID + deletingContractPaymentId];

    if (deleteResponse?.status === REQUEST_SUCCESS) {
      setDeletingContractPaymentId(undefined);
      ContractPaymentService.getContractPayments(contractId);
    }

    if (deleteResponse?.status === REQUEST_ERROR) {
      notifications.show({
        title: t("bankSelection.paymentStructure.title"),
        message: t("error.unknown"),
        color: "red",
      });
      setDeletingContractPaymentId(undefined);
    }
  }, [contractId, deletingContractPaymentId, serviceObjects, t]);

  const handleCreateContractPayment = () => {
    if (!contractId || !newContractPaymentType || newContractPaymentAmount == null) return;

    const payload: ContractPaymentRequest = {
      contractId,
      type: newContractPaymentType,
      ...(newContractPaymentBank && { bank: newContractPaymentBank }),
      ...(newContractPaymentContractNumber && { contractNumber: newContractPaymentContractNumber }),
      amount: newContractPaymentAmount,
    };

    ContractPaymentService.createContractPayment(payload);
  };

  const handleDeleteContractPayment = (paymentId: number) => {
    setDeletingContractPaymentId(paymentId);
    ContractPaymentService.deleteContractPayment(paymentId);
  };

  const resetNewContractPaymentForm = () => {
    setIsAddingContractPayment(false);
    setNewContractPaymentType(null);
    setNewContractPaymentBank(null);
    setNewContractPaymentContractNumber("");
    setNewContractPaymentAmount(undefined);
  };

  const isDeletingPayment = (paymentId: number) =>
    serviceObjects[REQUEST_CONTRACT_PAYMENT_DELETE_ID + paymentId]?.status === REQUEST_PENDING;

  const contractPaymentsTotalAmount = contractPayments.reduce(
    (sum, payment) => sum + payment.amount,
    0
  );

  return {
    contractPayments,
    contractPaymentTypes,
    isAddingContractPayment,
    newContractPaymentType,
    newContractPaymentBank,
    newContractPaymentContractNumber,
    newContractPaymentAmount,
    isCreatingContractPayment: contractPaymentCreateResponse?.status === REQUEST_PENDING,
    contractPaymentsTotalAmount,
    setIsAddingContractPayment,
    setNewContractPaymentType,
    setNewContractPaymentBank,
    setNewContractPaymentContractNumber,
    setNewContractPaymentAmount,
    handleCreateContractPayment,
    handleDeleteContractPayment,
    resetNewContractPaymentForm,
    isDeletingPayment,
  };
};
